import { Divider, FormControl, TextField, Theme, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { Formik, FormikHelpers, Field, FieldProps, FormikProps, Form } from 'formik';
import { useContext, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { IAuthUserMFAOptions, IDropdownResponse } from '../../models';
import { MinimalPage, Button, RedirectUser, Loader } from '../../components';
import {
  login,
  getUserMfaOptions,
  getUserMfaTypes,
  getMe,
  getLoginFlow,
  sendResetLink
} from '../../fetch';
import { UserContext } from '../../context';
import { MFAFlow } from './mfa-flow';
import { useFlags } from '../../hooks';
import { useMsal } from '@azure/msal-react';
import { useLocation } from 'react-router-dom';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';

interface ILoginValues {
  email: string;
  password: string;
}

const LoginSchema = Yup.object().shape({
  email: Yup.string().required('Required'),
  password: Yup.string().required('Required')
});

export const Login = () => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  // state
  const [existingMfaOptions, setExistingMfaOptions] = useState<IAuthUserMFAOptions[] | null>(null);
  const [mfaTypes, setMFATypes] = useState<IDropdownResponse[] | null>(null);
  // context
  const { user, isLoading, setUser, isEntraLoading } = useContext(UserContext);

  const { state }: { state: any; search: string } = useLocation();
  const msalRedirectConfig = {
    redirectStartPage: `${state?.from?.pathname ?? ''}${state?.from?.search ?? ''}` ?? `/`,
    scopes: ['User.Read'],
    prompt: 'select_account'
  };
  const { instance } = useMsal();
  const featureFlags = useFlags();

  const handleGetLoginFlow = async (email: string) => {
    setLoading(true);
    try {
      const res = await getLoginFlow({ email: email });
      if (res?.isEntraUser) {
        instance.loginRedirect(msalRedirectConfig);
      } else {
        setShowPassword(true);
      }
      setLoading(false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    document.title = `Login | BuilderLinq`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isEntraLoading) {
    return <Loader type="fullscreen" position="centered" />;
  }
  if (isLoading) {
    return null;
  }

  // have flag and we have a user and they are loaded redirect to correct page
  if (user && !isLoading) {
    return <RedirectUser />;
  }
  return (
    <MinimalPage title="Login">
      <MFAFlow
        setUser={setUser}
        existingMfaOptions={existingMfaOptions}
        resetLogin={() => {
          setExistingMfaOptions(null);
          setMFATypes(null);
        }}
        mfaTypes={mfaTypes}
      />
      {!mfaTypes && !existingMfaOptions && (
        <Formik
          initialValues={{
            email: '',
            password: ''
          }}
          onSubmit={async (values: ILoginValues, actions: FormikHelpers<ILoginValues>) => {
            try {
              const res = await login({
                userName: values.email,
                password: values.password
              });
              // user has mfa activated so trigger mfa flow
              if (res.roles.includes('MFA_Setup_Required') || res.roles.includes('MFA_Required')) {
                const [userMfaOptions, mfaTypes] = await Promise.all([
                  getUserMfaOptions({ isActive: true }),
                  getUserMfaTypes()
                ]);
                setExistingMfaOptions(userMfaOptions);
                setMFATypes(mfaTypes);
              } else {
                const user = await getMe();
                setUser(user);
              }
            } catch (error) {
              // if we get a message to reset password send reset link to users account
              if (error?.Detail?.includes('Please reset your password')) {
                await sendResetLink({
                  email: values.email
                });
                enqueueSnackbar(
                  `${error?.Detail} An email has been sent to your account with a link to reset your password.`,
                  {
                    variant: 'error'
                  }
                );
              } else {
                enqueueSnackbar(error?.Detail ?? 'Error logging in, please try again', {
                  variant: 'error'
                });
              }
              actions.resetForm();
              const input = document.getElementById('email');
              if (input) {
                input.focus();
              }
            } finally {
              actions.setSubmitting(false);
            }
          }}
          validationSchema={LoginSchema}
        >
          {(formikProps: FormikProps<ILoginValues>) =>
            featureFlags.uniformLoginPage ? (
              <Form data-testid="admin-login-form" onSubmit={formikProps.handleSubmit}>
                <FormControl margin="normal" fullWidth>
                  <Field name="email">
                    {({ field, form }: FieldProps<ILoginValues>) => (
                      <TextField
                        {...field}
                        error={
                          form.touched.email && form.errors && form.errors.email ? true : false
                        }
                        fullWidth={true}
                        helperText={form.touched.email && form.errors && form.errors.email}
                        id="email"
                        label={showPassword ? 'Username' : 'Email'}
                        margin="none"
                        required={true}
                        type="text"
                        value={formikProps.values.email}
                        autoComplete="on"
                      />
                    )}
                  </Field>
                </FormControl>
                {showPassword && (
                  <FormControl margin="normal" fullWidth>
                    <Field name="password">
                      {({ field, form }: FieldProps<ILoginValues>) => (
                        <TextField
                          {...field}
                          error={
                            form.touched.password && form.errors && form.errors.password
                              ? true
                              : false
                          }
                          fullWidth={true}
                          helperText={form.touched.password && form.errors && form.errors.password}
                          id="password"
                          label="Password"
                          margin="none"
                          required={true}
                          type="password"
                          value={formikProps.values.password}
                          autoComplete="on"
                        />
                      )}
                    </Field>
                  </FormControl>
                )}
                {!showPassword && (
                  <FormControl margin="normal" fullWidth>
                    <Button
                      id="get-login-flow"
                      size="large"
                      disabled={!formikProps.values.email || formikProps.isSubmitting || loading}
                      loading={loading ? 'NEXT' : ''}
                      variant="contained"
                      color="primary"
                      onClick={() => handleGetLoginFlow(formikProps.values.email)}
                    >
                      Next
                    </Button>
                  </FormControl>
                )}
                {showPassword && (
                  <>
                    <FormControl margin="normal" fullWidth>
                      <Button
                        color="primary"
                        disabled={
                          !formikProps.dirty || formikProps.isSubmitting || !formikProps.isValid
                        }
                        id="submit"
                        loading={formikProps.isSubmitting ? 'Logging in...' : ''}
                        size="large"
                        type="submit"
                        variant="contained"
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            formikProps.handleSubmit();
                          }
                        }}
                      >
                        {!formikProps.isSubmitting ? 'Login' : ''}
                      </Button>
                    </FormControl>

                    <FormControl
                      className={classes.forgotPasswordControl}
                      fullWidth={true}
                      margin="normal"
                    >
                      <Button
                        className={classes.forgotPasswordAndReturn}
                        color="primary"
                        fullWidth={true}
                        id="forgot-password"
                        variant="text"
                        asLink={true}
                        to={'/forgot-password'}
                      >
                        Forgot Password?
                      </Button>
                    </FormControl>

                    <Button
                      className={classes.forgotPasswordAndReturn}
                      color="primary"
                      fullWidth={true}
                      id="return"
                      variant="text"
                      onClick={() => setShowPassword(false)}
                      startIcon={<KeyboardReturnIcon />}
                    >
                      Return
                    </Button>
                  </>
                )}
              </Form>
            ) : (
              <Form data-testid="admin-login-form" onSubmit={formikProps.handleSubmit}>
                <FormControl margin="normal" fullWidth>
                  <Field name="email">
                    {({ field, form }: FieldProps<ILoginValues>) => (
                      <TextField
                        {...field}
                        error={
                          form.touched.email && form.errors && form.errors.email ? true : false
                        }
                        fullWidth={true}
                        helperText={form.touched.email && form.errors && form.errors.email}
                        id="email"
                        label="User Name"
                        margin="none"
                        required={true}
                        type="text"
                        value={formikProps.values.email}
                        autoComplete="on"
                      />
                    )}
                  </Field>
                </FormControl>
                <FormControl margin="normal" fullWidth>
                  <Field name="password">
                    {({ field, form }: FieldProps<ILoginValues>) => (
                      <TextField
                        {...field}
                        error={
                          form.touched.password && form.errors && form.errors.password
                            ? true
                            : false
                        }
                        fullWidth={true}
                        helperText={form.touched.password && form.errors && form.errors.password}
                        id="password"
                        label="Password"
                        margin="none"
                        required={true}
                        type="password"
                        value={formikProps.values.password}
                        autoComplete="on"
                      />
                    )}
                  </Field>
                </FormControl>
                <FormControl margin="normal" fullWidth>
                  <Button
                    color="primary"
                    disabled={
                      !formikProps.dirty || formikProps.isSubmitting || !formikProps.isValid
                    }
                    id="submit"
                    loading={formikProps.isSubmitting ? 'Logging in...' : ''}
                    size="large"
                    type="submit"
                    variant="contained"
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        formikProps.handleSubmit();
                      }
                    }}
                  >
                    {!formikProps.isSubmitting ? 'Login' : ''}
                  </Button>
                </FormControl>
                <FormControl
                  className={classes.forgotPasswordControl}
                  fullWidth={true}
                  margin="normal"
                >
                  <Button
                    className={classes.forgotPasswordAndReturn}
                    color="primary"
                    fullWidth={true}
                    id="forgot-password"
                    variant="text"
                    asLink={true}
                    to={'/forgot-password'}
                  >
                    Forgot Password?
                  </Button>
                </FormControl>

                {featureFlags?.entraId && (
                  <>
                    <Divider />
                    <Typography component={'div'} className={classes.helperText} variant="body2">
                      <Typography variant="body2" fontWeight={'bold'} display="inline">
                        Employees Only
                      </Typography>{' '}
                      - Click below to log into BuilderLinq with your Microsoft account
                    </Typography>
                    <FormControl margin="normal" fullWidth>
                      <Button
                        color="primary"
                        id="entra-id-login"
                        size="large"
                        variant="contained"
                        type="button"
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            instance.loginRedirect(msalRedirectConfig);
                          }
                        }}
                        onClick={() => {
                          instance.loginRedirect(msalRedirectConfig);
                        }}
                      >
                        {'Login with SSO'}
                      </Button>
                    </FormControl>
                  </>
                )}
              </Form>
            )
          }
        </Formik>
      )}
    </MinimalPage>
  );
};

const useStyles = makeStyles((theme: Theme) => {
  return {
    forgotPasswordControl: {
      marginTop: theme.spacing(0.5)
    },
    forgotPasswordAndReturn: {
      marginTop: theme.spacing(0),
      textDecoration: 'underline',
      textTransform: 'none',
      '&:hover': {
        textDecoration: 'underline'
      }
    },
    helperText: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(0.5)
    }
  };
});
